import { useEffect } from 'react';

import { type QueryClient, useQuery } from '@tanstack/react-query';
import {
	type LoaderFunctionArgs,
	Navigate,
	Outlet,
	redirect
} from 'react-router-dom';

import AppHeader from '@/components/app-header';
import { userQueries } from '@/queries/user/user.queries';
import { useAuthStore } from '@/store';
import { Roles } from '@/types/roles';

export const HardRedirect = ({ to }: { to: string }) => {
	useEffect(() => {
		globalThis.location.href = to;
	}, [to]);
	return null;
};

export const LayoutLoader =
	(queryClient: QueryClient) =>
	async ({ request }: LoaderFunctionArgs) => {
		const token = useAuthStore.getState().id_token;
		const url = new URL(request.url);

		const code = url.searchParams.get('code');

		if (!token && !code) {
			return redirect('/login');
		}

		if (code) return null;

		queryClient.prefetchQuery(userQueries.user());

		return null;
	};

export default function Layout() {
	const token = useAuthStore((s) => s.id_token);
	const logout = useAuthStore((s) => s.logout);

	const user = useQuery(userQueries.user());

	useEffect(() => {
		if (user.status === 'error') {
			logout();
		}
	}, [user.status]);

	if (!token || user.error) {
		return <Navigate to="/login" replace />;
	}

	if (user.isPending) {
		return (
			<div className="mx-auto flex h-screen w-screen flex-col items-center justify-center overflow-hidden">
				<div className="loader mx-auto my-3 h-10 w-10 rounded-full border-4 border-t-4 border-gray-200 text-center ease-linear"></div>
				<h2 className="text-md text-center font-semibold text-black">
					Logging in...
				</h2>
			</div>
		);
	}

	const allowedRoles = [
		Roles.AccountManager,
		Roles.Procurement,
		Roles.Compliance,
		Roles.VendorAuthorizedOfficer
	];

	const hasValidRole =
		user.data?.data.roleId && allowedRoles.includes(user.data?.data.roleId);

	if (!hasValidRole) {
		return <HardRedirect to="/cms/" />;
	}

	return (
		<div className="min-h-svh w-full overflow-y-hidden">
			<AppHeader />
			<div className="bg-background flex h-[calc(100vh-4rem)] flex-col gap-4 overflow-x-hidden overflow-y-auto p-4 pt-0 group-has-data-[collapsible=icon]/sidebar-wrapper:h-[calc(100vh-3rem)]">
				<Outlet />
			</div>
		</div>
	);
}
